<script lang="ts" setup>
import { computed, navigateTo } from '#imports';
import laughEmoji from '~/assets/image/index/emoji-laugh.svg';
import RadialProgress from '~/components/RadialProgress.vue';
import UserHoverCard from '~/components/UserHoverCard/UserHoverCard.vue';
import { getBackgroundColor } from '~/utils/utility';

interface Difficulty {
  color?: string;
  id?: number;
  name?: string;
}

interface OwnerVo {
  authenticationIcon?: string;
  avatar?: string;
  ccfLevel?: number;
  nickname?: string;
  uid?: string;
}

const props = withDefaults(
  defineProps<{
    target?: string; // 1. 将 target 设为可选属性
    gid?: number;
    pid: number;
    title: string;
    problemId: string;
    percent: number;
    tags: string[];
    difficulty: Difficulty;
    ownerVo: OwnerVo;
    likeCount: number;
  }>(),
  {
    gid: undefined,
    target: '_self', // 2. 设置默认值
  },
);

const acPercent = computed(() => {
  return Math.round(props.percent * 1000) / 10; // 例如 0.856 → 85.6
});

const handleClick = () => {
  navigateTo(
    {
      name: 'oj_problem_detail',
      query: {
        pid: props.pid,
        gid: props.gid,
      },
    },
    {
      open: {
        target: props.target,
      },
    },
  );
  console.log('click');
};
</script>
<template>
  <div
    class="w-full cursor-pointer rounded border border-colorFillSecondary px-4 py-[14px] text-xs text-[theme(colors.colorTextSecondary)] !transition-colors !duration-500 hover:border-colorText"
    @click="handleClick"
  >
    <div class="flex items-center">
      <!-- 标题 -->
      <h6
        :title="title"
        class="inline-block max-w-[77%] overflow-hidden text-ellipsis whitespace-nowrap text-base font-bold text-[theme(colors.font1)]"
      >
      {{ problemId }}｜{{ title }}
      </h6>
      <!-- 标题 end-->

      <div class="ml-auto flex items-center">
        <div class="mr-1">{{ acPercent }}%</div>
        <RadialProgress :percentage="acPercent" />
      </div>
    </div>

    <div class="mt-3 flex items-center gap-2 overflow-hidden">
      <span
        :style="{
          color: difficulty.color,
          backgroundColor: getBackgroundColor(difficulty.color, 0.2),
        }"
        class="tag"
      >
        {{ difficulty.name }}
      </span>

      <span v-for="tag in tags" :key="tag" class="tag">
        {{ tag }}
      </span>
    </div>

    <div
      class="mt-4 flex h-5 items-center text-[theme(colors.colorTextTertiary)]"
    >
      <div class="flex items-center">
        <span class="mr-1">出题人</span>

        <UserHoverCard v-if="ownerVo.uid" :uid="ownerVo.uid">
          <NuxtLink
            class="flex items-center"
            :to="{
              name: 'oj_home',
              query: {
                id: ownerVo.uid,
              },
            }"
            @click.stop
            ><a-avatar
              class="!mr-1 border-solid !border-[#ededed]"
              :size="16"
              :src="ownerVo.avatar"
            ></a-avatar>
            <span
              class="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-font3"
              >{{ ownerVo.nickname }}</span
            >
          </NuxtLink>
        </UserHoverCard>
        <span
          v-else-if="ownerVo.nickname && ownerVo.uid === null"
          class="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap text-font3"
          >{{ ownerVo.nickname }}</span
        >
      </div>
      <div v-if="likeCount > 0" class="ml-auto flex items-center">
        <img class="mr-1 w-4" :src="laughEmoji" alt="laugh" />
        <span>好题：{{ likeCount }}</span>
      </div>
    </div>
  </div>
</template>
<style scoped>
.tag {
  @apply flex-center h-[22px] flex-shrink-0 rounded-sm bg-[#f3f3f3] px-[6px] text-xs text-[theme(colors.font2)];
}
</style>
